import { types, flow, applySnapshot } from 'mobx-state-tree'
import buildUrlFromOptions from 'src/utils/buildUrlFromOptions'
import axios from 'axios'

export const ProductSpecifications = types.model({
    id: types.identifierNumber,
    title: types.string,
})
export type IProductSpecifications = typeof ProductSpecifications.Type

export const ProductOption = types.model({
    id: types.identifierNumber,
    title: types.string,
})
export type IProductOption = typeof ProductOption.Type

export const Brand = types.model({
    id: types.identifierNumber,
    title: types.string,
})
export type IBrand = typeof Brand.Type

export const Type = types.model({
    id: types.identifierNumber,
    title: types.string,
})
export type IType = typeof Type.Type

export const Product = types.model({
    id: types.identifierNumber,
    name: types.string,
    path: types.string,
    title: types.maybe(types.string),
    reference: types.maybe(types.string),
    category: types.maybe(types.string),
    availability: types.maybe(types.string),
    productImages: types.optional(types.array(types.string), []),

    brand: types.maybe(Brand),
    type: types.maybe(Type),
    label: types.maybe(types.string),
    location: types.maybe(types.string),
    priceOld: types.maybeNull(types.number),
    formattedPriceOld: types.maybeNull(types.string),
    price: types.maybeNull(types.number),
    formattedPrice: types.maybeNull(types.string),
    priceOutlet: types.maybeNull(types.number),
    formattedPriceOutlet: types.maybeNull(types.string),
    priceFrom: types.maybeNull(types.number),
    formattedPriceFrom: types.maybeNull(types.string),
    width: types.maybe(types.string),
    length: types.maybe(types.string),
    numberOfBedrooms: types.maybe(types.number),
    pwSort: types.maybe(types.number),

    content: types.maybe(types.string),
    specifications: types.maybe(types.array(ProductSpecifications)),
    options: types.maybe(types.array(ProductOption)),
    title2: types.maybe(types.string),
    content2: types.maybe(types.string),

    created: types.maybe(types.number)
})
export type IProduct = typeof Product.Type

// const FilterOptionMultiselectId = types.model({
//     id: types.number,
//     title: types.string,
//     count: types.number,
// })

// const FilterOptionMultiselect = types.model({
//     title: types.string,
//     count: types.number,
// })

// export const FilterMultiselectId = types.model({
//     name: types.string,
//     type: types.literal('multiselect_multi'),
//     whitelist: types.maybe(types.array(types.number)),
//     options: types.maybe(types.array(FilterOptionMultiselectId)),
// })
// export type IFilterMultiselectId = typeof FilterMultiselectId.Type

// export const FilterMultiselect = types.model({
//     name: types.string,
//     type: types.literal('multiselect_single'),
//     options: types.maybe(types.array(FilterOptionMultiselect)),
// })
// export type IFilterMultiselect = typeof FilterMultiselect.Type

// export const FilterRange = types.model({
//     name: types.string,
//     type: types.literal('range'),
//     min: types.number,
//     max: types.number,
// })
// export type IFilterRange = typeof FilterRange.Type

// export const FilterUnionTypes = types.union(FilterMultiselectId, FilterMultiselect, FilterRange)

export const FilterItem = types.model({
    id: types.number,
    title: types.string,
    count: types.maybe(types.number),
    selected: types.optional(types.boolean, false),
    show: types.optional(types.boolean, false),
})

export const ProductList = types
    .model({
        items: types.optional(types.array(Product), []),
        pages: types.optional(types.integer, 0),
        page: types.optional(types.integer, 1),
        entries: types.optional(types.integer, 0),
        sort: types.optional(types.string, 'default'),

        filterCategories: types.array(FilterItem),
        filterSpecifications: types.array(FilterItem),
        filterBedrooms: types.array(FilterItem),
        filterLocations: types.array(FilterItem),
        filterBrands: types.array(FilterItem),
        filterMinPrice: types.maybeNull(types.number),
        filterMaxPrice: types.maybeNull(types.number),
        filterTypes: types.array(FilterItem),

        currentLanguageId: types.number,
        currentPageId: types.number,
    })
    .volatile(self => ({
        minprice: self.filterMinPrice,
        maxprice: self.filterMaxPrice,
    }))
    .extend(self => {
        // let minprice: number = self.filterMinPrice
        // let maxprice: number = self.filterMaxPrice

        let selectedCategories = self.filterCategories.filter(s => s.selected).map(s => s.id)
        let selectedSpecifications = self.filterSpecifications.filter(s => s.selected).map(s => s.id)
        let selectedBedrooms = self.filterBedrooms.filter(s => s.selected).map(s => s.id)
        let selectedLocations = self.filterLocations.filter(s => s.selected).map(s => s.id)
        let selectedBrands = self.filterBrands.filter(s => s.selected).map(s => s.id)
        let selectedTypes = self.filterTypes.filter(s => s.selected).map(s => s.id)

        const CancelToken = axios.CancelToken
        let cancelLoad: any

        const load = flow(function* load() {
            try {
                typeof cancelLoad == 'function' && cancelLoad()
                const response = yield axios.get(
                    `/filterdata.php?` +
                        buildUrlFromOptions({
                            page: self.page,
                            sort: self.sort,
                            minprice: self.minprice,
                            maxprice: self.maxprice,
                            selectedCategories,
                            selectedSpecifications,
                            selectedBedrooms,
                            selectedLocations,
                            selectedBrands,
                            selectedTypes,
                            currentLanguageId: self.currentLanguageId,
                            currentPageId: self.currentPageId
                        }),
                    {
                        cancelToken: new CancelToken(c => {
                            cancelLoad = c
                        }),
                    }
                )
                const data = response.data
                applySnapshot(self, data)
            } catch (e) {}
        })

        const updateVolatileMemory = () => {
            self.minprice = self.filterMinPrice
            self.maxprice = self.filterMaxPrice
            selectedCategories = self.filterCategories.filter((s: any) => s.selected).map((s: any) => s.id)
            selectedSpecifications = self.filterSpecifications.filter((s: any) => s.selected).map((s: any) => s.id)
            selectedBedrooms = self.filterBedrooms.filter((s: any) => s.selected).map((s: any) => s.id)
            selectedLocations = self.filterLocations.filter((s: any) => s.selected).map((s: any) => s.id)
            selectedBrands = self.filterBrands.filter((s: any) => s.selected).map((s: any) => s.id)
            selectedTypes = self.filterTypes.filter((s: any) => s.selected).map((s: any) => s.id)
        }

        const sortBy = (field: string, direction: string) => {
            self.sort = (direction == 'desc' ? '-' : '') + field
            self.page = 1
            load()
        }

        const goToPage = (newPage: number) => {
            self.page = newPage
            load()
        }

        const toggleCategory = (id: number) => {
            updateVolatileMemory()
            const pos = selectedCategories.indexOf(id)
            if (pos > -1) {
                selectedCategories.splice(pos, 1)
                load()
            } else {
                selectedCategories.push(id)
                load()
            }
        }

        const toggleSpecification = (id: number) => {
            updateVolatileMemory()
            const pos = selectedSpecifications.indexOf(id)
            if (pos > -1) {
                selectedSpecifications.splice(pos, 1)
                load()
            } else {
                selectedSpecifications.push(id)
                load()
            }
        }

        const toggleBedroom = (id: number) => {
            updateVolatileMemory()
            const pos = selectedBedrooms.indexOf(id)
            if (pos > -1) {
                selectedBedrooms.splice(pos, 1)
                load()
            } else {
                selectedBedrooms.push(id)
                load()
            }
        }

        const toggleLocation = (id: number) => {
            updateVolatileMemory()
            const pos = selectedLocations.indexOf(id)
            if (pos > -1) {
                selectedLocations.splice(pos, 1)
                load()
            } else {
                selectedLocations.push(id)
                load()
            }
        }

        const toggleBrand = (id: number) => {
            updateVolatileMemory()
            const pos = selectedBrands.indexOf(id)
            if (pos > -1) {
                selectedBrands.splice(pos, 1)
                load()
            } else {
                selectedBrands.push(id)
                load()
            }
        }

        const toggleType = (id: number) => {
            updateVolatileMemory()
            const pos = selectedTypes.indexOf(id)
            if (pos > -1) {
                selectedTypes.splice(pos, 1)
                load()
            } else {
                selectedTypes.push(id)
                load()
            }
        }

        const setMinPrice = (price: number | null) => {
            self.minprice = price
            self.filterMinPrice = price
            load()
        }

        const setMaxPrice = (price: number | null) => {
            self.maxprice = price
            self.filterMaxPrice = price
            load()
        }

        return {
            views: {
                get inputFieldMinPrice() {
                    return self.minprice
                },
                get inputFieldMaxPrice() {
                    return self.maxprice
                },
            },
            actions: {
                sortBy,
                load,
                goToPage,
                toggleCategory,
                toggleSpecification,
                toggleBedroom,
                toggleLocation,
                toggleBrand,
                toggleType,
                setMinPrice,
                setMaxPrice,
            },
        }
    })
export type IProductList = typeof ProductList.Type



// WEBPACK FOOTER //
// ./react/src/models/product.ts